import type { StateCreator } from 'zustand';
import type { Currency, CurrencyApi } from '../../graphql';
import type { GlobalStore } from './store';
import { globalStore, useGlobalStore } from './store';

export {
  convertMonetaryValue,
  type CurrencyRates,
  type MonetaryValue,
} from '@seaber/common/lib/currency';

export type CurrencyRatesSlice = {
  currencyRates: CurrencyApi | null;
  setCurrencyRates: (
    currency: CurrencyApi['baseCurrency'],
    rates: CurrencyApi['rates'],
  ) => Promise<void>;
};

export const createCurrencyRateSlices: StateCreator<CurrencyRatesSlice> = (
  setState,
) => ({
  currencyRates: null,
  setCurrencyRates: async (
    currency: CurrencyApi['baseCurrency'],
    rates: CurrencyApi['rates'],
  ) => {
    setState({ currencyRates: { rates, baseCurrency: currency } });
  },
});

/*
 * Memoized selectors
 * https://docs.pmnd.rs/zustand/recipes/recipes#memoizing-selectors
 */
const currencyRatesSelector = (s: GlobalStore) => s.currencyRates;
const setCurrencyRatesSelector = (s: GlobalStore) => s.setCurrencyRates;

export function useCurrencyRates() {
  return useGlobalStore(currencyRatesSelector);
}

export function useSetCurrencyRates() {
  return useGlobalStore(setCurrencyRatesSelector);
}

/**
 * Utility functions
 */
export function convertToDefaultCurrency(amount: number, _currency?: string) {
  // State doesn't work in worker threads.
  if (globalThis.window === undefined)
    throw new Error('convertToDefaultCurrency called in worker thread');

  const currency = _currency?.toUpperCase();
  const state = globalStore.getState();

  if (!currency || currency === state.defaultCurrency) return amount;

  const rates = state.currencyRates?.rates;
  if (!rates) {
    console.error('convertToDefaultCurrency called with no currency rates');
    return null;
  }

  const rate = rates.find((r) => r?.code === currency)?.value;

  if (!rate) {
    console.error('rate not available for currency', currency);
    return amount;
  }

  return amount / rate;
}

export function getCurrencyRate(rate: Currency | string) {
  // State doesn't work in worker threads.
  if (globalThis.window === undefined)
    throw new Error('getCurrencyRate called in worker thread');

  const { currencyRates } = globalStore.getState();

  if (!currencyRates) {
    console.error('getCurrencyRate called with no currency rates');
    return null;
  }

  return currencyRates?.rates?.find((r) => r?.code === rate)?.value;
}
